<template>
  <div class="d-flex flex-column text-center">
    <span class="text-overline ma-8">{{
      $vuetify.lang.t("$vuetify.worksheet.chooseLocation")
    }}</span>

    <v-autocomplete
      :label="$vuetify.lang.t('$vuetify.worksheet.location')"
      placeholder="Choose a location for inventory"
      v-model="locationId"
      :items="filteredLocations"
      item-text="name"
      item-value="id"
      clearable
      solo
      hide-details
    />

    <div class="d-flex justify-center mx-2 my-8">
      <v-btn :disabled="worksheet.status  == 'USED'" @click="updateWorksheet(worksheet)" color="primary">{{
        $vuetify.lang.t("$vuetify.base.update")
      }}</v-btn>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
const VehicleSelect = () =>
  import("@/components/companyVehicle/VehicleSelect.vue");
import { getCompanyVehicle } from "@/services/companyVehicle";

export default {
  components: {
    VehicleSelect,
  },
  props: {
    worksheet: {
      type: Object,
      required: true,
    },
    weightUnit: {
      type: String,
      required: false,
      default: 'Kg'
    }
  },
  data: () => {
    return {
      locationId: null,
      productLocations: null, // null = not loaded yet, [] = loaded but empty, [...] = loaded with data
    };
  },
  computed: {
    ...mapGetters("auth", ["currentUser"]),
    ...mapGetters("locations", ["locations"]),

    hasEntries() {
      return this.worksheet.entries && this.worksheet.entries.length > 0;
    },
    // Filter locations based on worksheet's associated products
    filteredLocations() {
      // If no product info available, show all locations
      if (!this.worksheet.productId && (!this.worksheet.products || this.worksheet.products.length === 0)) {
        return this.locations;
      }
      
      // If we attempted to load product-specific locations, use the result
      // (even if empty - means no locations have this product)
      if (this.productLocations !== null) {
        return this.productLocations;
      }
      
      // Otherwise show all locations as fallback (still loading)
      return this.locations;
    },
  },
  async created() {
    await this.loadLocations();
    this.locationId = this.worksheet.locationId;
    await this.loadProductLocations();
  },
  methods: {
    ...mapActions("processWorksheet", [
      "updateProcessWorksheet",
      "deleteProcessWorksheet",
    ]),
    ...mapActions("messages", ["addErrorMessage", "addSuccessMessage"]),
    ...mapActions("locations", ["loadLocations"]),

    async loadProductLocations() {
      // Get product IDs from worksheet
      const productIds = [];
      if (this.worksheet.productId) {
        productIds.push(this.worksheet.productId);
      } else if (this.worksheet.products && this.worksheet.products.length > 0) {
        productIds.push(...this.worksheet.products.map(p => p.id));
      }
      
      if (productIds.length === 0) {
        console.log('No products specified, showing all locations');
        return; // No products, use all locations
      }
      
      console.log('Loading locations for products:', productIds);
      
      try {
        // Use the new efficient endpoint that returns only location IDs
        // Spring Boot expects repeated param names (productIds=1&productIds=2), not brackets
        const response = await this.$axios.get('/inventory/locations-by-products', {
          params: { productIds },
          paramsSerializer: params => {
            // Serialize array parameters without brackets for Spring Boot compatibility
            return Object.keys(params)
              .map(key => {
                const value = params[key];
                if (Array.isArray(value)) {
                  return value.map(v => `${key}=${encodeURIComponent(v)}`).join('&');
                }
                return `${key}=${encodeURIComponent(value)}`;
              })
              .join('&');
          }
        });
        
        console.log('API response - location IDs:', response.data);
        console.log('Available locations in store:', this.locations.length);
        
        // Always set productLocations based on the response
        if (response.data && response.data.length > 0) {
          // Filter locations to only those returned by the backend
          const locationIds = response.data;
          this.productLocations = this.locations.filter(loc => 
            locationIds.includes(loc.id)
          );
          
          console.log(`✓ Filtered to ${this.productLocations.length} locations with inventory for ${productIds.length} product(s)`);
          console.log('Filtered location IDs:', this.productLocations.map(l => l.id));
        } else {
          // No locations have this product - show empty list
          this.productLocations = [];
          console.log(`⚠ No locations found with inventory for ${productIds.length} product(s)`);
        }
      } catch (error) {
        console.error('Error loading product locations:', error);
        this.productLocations = null; // null means error/not loaded, fallback to all locations
      }
    },

    updateWorksheet(worksheet) {
      this.worksheet.locationId = this.locationId;

      const payload = Object.assign({}, worksheet);
      delete payload.files;
      this.updateProcessWorksheet(payload);

      this.$emit("update:worksheet", worksheet);
      this.$emit("updated", worksheet);
    },
  },
};
</script>

<style></style>
